import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { Error } from "../../common/components/AppState/Error";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import { Input } from "../../common/components/Input";
import OrganizationInputStyle from "../../common/components/Input/OrganizationInputStyle";
import ModalConfirmBeforeNavigatingOut from "../../common/components/Modal/ModalConfirmBeforeNavigatingOut";
import { closeStandardModal } from "../../common/state/slice/modal/modalSlice";
import type { RootState } from "../../common/state/store";
import { useCallbackPrompt } from "../../common/util/useCallbackPrompt";
import { setAlert } from "../../features/alert/state/alertsSlice";
import ModalGallery from "../../features/graph/components/ModalGallery";
import {
  fetchMoodleEnv,
  getIsAdminOfOrganization,
  getMoodleEnv,
  setCurrentUserAndOrg,
  updateOrganization,
} from "../../features/profile/state/profileSlice";
import { getCurrentOrganization, getCurrentUser } from "../../features/profile/state/profileSlice";
import RoundImagePreview from "../../features/projects/components/RoundImagePreview";
import { allTeamSelector, getTeam } from "../../features/team/state/teamSlice";
import { getSubscriptionFeature } from "src/features/subscription/state/subscriptionSlice";
import type { IOrganization, ISource } from "../../model/model";
import type { IMedia } from "../../model/unityObject";
import MoodleManagement from "src/common/components/MoodleManagement/MoodleManagement";

export const ORGANIZATION_COMPONENT_ID = "organizationComponent";
export const ORGPHOTO_COMPONENT_ID = "OrganizationPhotoComponent";

type FormValues = {
  name: string;
  source_logo: number;
  ai_context_sector: string;
  ai_context_description: string;
};

const Organization = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { currentUser, isAdmin, organization, team } = useAppSelector((state: RootState) => {
    return {
      currentUser: getCurrentUser(state),
      organization: getCurrentOrganization(state),
      team: allTeamSelector(state),
      isAdmin: getIsAdminOfOrganization(state),
    };
  });

  useEffect(() => {
    if (organization?.id) {
      void dispatch(getTeam({ orgId: organization.id, componentId: ORGANIZATION_COMPONENT_ID }));
    }
  }, [dispatch, organization?.id]);

  const moodleEnv = useAppSelector(getMoodleEnv);

  const moodleFeature = useAppSelector((state: RootState) =>
    getSubscriptionFeature(state, "moodle-integration"),
  );

  useEffect(() => {
    if (!currentUser || !organization) {
      void dispatch(setCurrentUserAndOrg({ componentId: ORGANIZATION_COMPONENT_ID })).then(
        (action) => {
          if (!setCurrentUserAndOrg.fulfilled.match(action)) {
            return;
          }
          const fetchedOrganization = action.payload.organization;
          const aiCtx = getAiContext(fetchedOrganization);
          reset({
            name: fetchedOrganization?.name,
            source_logo: Number(fetchedOrganization?.source_logo?.id),
            ai_context_sector: aiCtx.sector,
            ai_context_description: aiCtx.description,
          });
          if (moodleFeature?.IsEnabled && isAdmin && fetchedOrganization) {
            void dispatch(fetchMoodleEnv({ organizationId: fetchedOrganization.id }));
          }
        },
      );
    } else {
      if (moodleFeature?.IsEnabled && isAdmin) {
        void dispatch(fetchMoodleEnv({ organizationId: organization.id }));
      }
    }
  }, []);

  useEffect(() => {
    const aiCtx = getAiContext(organization);
    reset({
      name: organization?.name,
      source_logo: Number(organization?.source_logo?.id),
      ai_context_sector: aiCtx.sector,
      ai_context_description: aiCtx.description,
    });
  }, []);

  useEffect(() => {
    dispatch(closeStandardModal("modal-gallery"));
  }, []);

  const [disabledName, setDisabledName] = useState(true);
  const [disabledSourceLogo, setDisabledSourceLogo] = useState(true);
  const [disabledEmail, setDisabledEmail] = useState(true);
  const [disabledAiSector, setDisabledAiSector] = useState(true);
  const [disabledAiDescription, setDisabledAiDescription] = useState(true);

  const getAiContext = (org: IOrganization | undefined) => {
    const ctx = org?.ai_context;
    if (!ctx) return { sector: "", description: "" };
    return { sector: ctx.sector ?? "", description: ctx.description ?? "" };
  };

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      name: organization?.name,
      source_logo: Number(organization?.source_logo?.id),
      ai_context_sector: getAiContext(organization).sector,
      ai_context_description: getAiContext(organization).description,
    },
  });

  const onSubmit = (data: FormValues) => {
    const updatedOrganization = {
      ...(organization as IOrganization),
      name: data.name,
      source_logo: {
        ...(organization?.source_logo as ISource),
        id: Number(data.source_logo),
      },
      ai_context: {
        sector: data.ai_context_sector?.trim() || undefined,
        description: data.ai_context_description?.trim() || undefined,
      },
    };
    dispatch(updateOrganization({ componentId: ORGANIZATION_COMPONENT_ID, ...updatedOrganization }))
      .then((res) => {
        if (!updateOrganization.fulfilled.match(res)) {
          return;
        }
        setDisabledName(true);
        setDisabledSourceLogo(true);
        setDisabledAiSector(true);
        setDisabledAiDescription(true);
        const savedAiCtx = getAiContext(res.payload);
        reset({
          name: res.payload?.name,
          source_logo: Number(res.payload?.source_logo?.id),
          ai_context_sector: savedAiCtx.sector,
          ai_context_description: savedAiCtx.description,
        });
        dispatch(
          setAlert({
            type: "success",
            msg: t("alerts.organizationEditSuccess"),
          }),
        );
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  };

  const cancel = () => {
    const savedAiCtx = getAiContext(organization);
    reset({
      name: organization?.name,
      source_logo: Number(organization?.source_logo?.id),
      ai_context_sector: savedAiCtx.sector,
      ai_context_description: savedAiCtx.description,
    });
    setDisabledName(true);
    setDisabledSourceLogo(true);
    setDisabledAiSector(true);
    setDisabledAiDescription(true);
  };

  const orgAdminPlaceholder = team.find(
    (user) => String(user.id) === String(organization?.admin.id),
  )?.email;

  const hasAnythingChanged =
    Number(organization?.source_logo?.id) !== watch("source_logo") || isDirty;

  const hasSomethingChanged = () => {
    if (organization?.name !== getValues("name")) {
      return true;
    }

    if (Number(organization?.source_logo?.id) !== getValues("source_logo")) {
      return true;
    }

    const savedAiCtx = getAiContext(organization);
    if (savedAiCtx.sector !== (getValues("ai_context_sector") || "")) {
      return true;
    }
    if (savedAiCtx.description !== (getValues("ai_context_description") || "")) {
      return true;
    }

    return false;
  };

  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    hasSomethingChanged(),
  );

  return (
    <>
      <ModalGallery
        nodeId="organization"
        onMediaChange={(e: IMedia) => {
          const selectedMediaId = Number(e.id);
          setValue("source_logo", selectedMediaId);
          dispatch(closeStandardModal("modal-gallery"));
        }}
        allowedMediaTypeOverride="image"
      />
      <div className="relative grow w-full pl-4 pr-4 px-4 mx-auto personalize-scroll-visible overflow-y-auto">
        <h2 className="text-2xl font-medium mb-2 mt-2 xl:mt-4">
          {t("pages.profile.tabs.myOrganization")}
        </h2>
        <h3 className="py-3">{t("pages.profile.organization.subheading")}</h3>
        <div className="mt-2 border-gray-300">
          <Error componentId={ORGANIZATION_COMPONENT_ID} />
          <IsLoading componentId={ORGANIZATION_COMPONENT_ID} showSuccess={false} spinnerPlaceholder>
            <form className="h-full mb-2" onSubmit={handleSubmit(onSubmit)}>
              <OrganizationInputStyle
                title={t("pages.profile.organization.orgName")}
                currUserId={currentUser?.id}
                disabled={disabledName}
                setDisabled={setDisabledName}
              >
                <Input
                  autoFocus
                  type="text"
                  highlight={disabledName}
                  readOnly={disabledName}
                  registerFct={() => register("name")}
                  className={`font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 max-w-max text-center sm:text-left border-0
                 `}
                  placeholder={t("pages.profile.organization.orgNamePlaceholder")}
                />
              </OrganizationInputStyle>

              <OrganizationInputStyle
                title={t("pages.profile.organization.companyLogo")}
                currUserId={currentUser?.id}
                disabled={disabledSourceLogo}
                setDisabled={setDisabledSourceLogo}
                setValueFct={setValue}
              >
                <RoundImagePreview
                  componentId={ORGPHOTO_COMPONENT_ID}
                  mediaId_OrIsItAnObjectLol={watch("source_logo")}
                  alt="Logo"
                  bigger
                />
              </OrganizationInputStyle>

              <OrganizationInputStyle
                title={t("pages.profile.organization.adminEmail")}
                currUserId={currentUser?.id}
                disabled={disabledEmail}
                setDisabled={setDisabledEmail}
                hidden
              >
                <Input
                  type="text"
                  readOnly={disabledEmail}
                  className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 max-w-max text-center sm:text-left border-0 ${ ? }"
                  placeholder={orgAdminPlaceholder ? orgAdminPlaceholder : "Anarchy"}
                />
              </OrganizationInputStyle>

              {isAdmin && (
                <>
                  <h3 className="text-lg font-medium mt-6 mb-3">
                    {t("pages.profile.organization.aiContextTitle")}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    {t("pages.profile.organization.aiContextDescription")}
                  </p>

                  <OrganizationInputStyle
                    title={t("pages.profile.organization.aiSector")}
                    currUserId={currentUser?.id}
                    disabled={disabledAiSector}
                    setDisabled={setDisabledAiSector}
                  >
                    <Input
                      type="text"
                      highlight={disabledAiSector}
                      readOnly={disabledAiSector}
                      registerFct={() => register("ai_context_sector")}
                      className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 flex-1 text-center sm:text-left border-0"
                      placeholder={t("pages.profile.organization.aiSectorPlaceholder")}
                    />
                  </OrganizationInputStyle>

                  <OrganizationInputStyle
                    title={t("pages.profile.organization.aiDescription")}
                    currUserId={currentUser?.id}
                    disabled={disabledAiDescription}
                    setDisabled={setDisabledAiDescription}
                  >
                    <Input
                      type="text"
                      highlight={disabledAiDescription}
                      readOnly={disabledAiDescription}
                      registerFct={() => register("ai_context_description")}
                      className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 flex-1 text-center sm:text-left border-0"
                      placeholder={t("pages.profile.organization.aiDescriptionPlaceholder")}
                    />
                  </OrganizationInputStyle>
                </>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-alternative-outline mr-4"
                  onClick={() => {
                    cancel();
                  }}
                >
                  {t("general.cancel")}
                </button>
                <button type="submit" className="btn-primary-fill" disabled={!hasAnythingChanged}>
                  {t("general.save")}
                </button>
              </div>
            </form>

            {moodleFeature?.IsEnabled && isAdmin && organization && currentUser ? (
              <MoodleManagement
                organization={organization}
                currentUser={currentUser}
                moodleEnv={moodleEnv}
                key={moodleEnv ? `${moodleEnv.url}|${moodleEnv.token}` : "none"}
              />
            ) : (
              <></>
            )}
          </IsLoading>
        </div>
      </div>
      <ModalConfirmBeforeNavigatingOut
        showDialog={showPrompt}
        cancelNavigation={cancelNavigation}
        confirmNavigation={confirmNavigation}
      />
    </>
  );
};

export default Organization;
